// MonacoModalEditor.jsx
import React, { useRef, useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import { Button, Stack } from "@mui/material";
/**
 * Props:
 * - open (bool) : show/hide modal
 * - initialValue (string) : initial code
 * - language (string) : "javascript" by default
 * - onSave(code) : function called when user saves (Ctrl/Cmd+S or Save button)
 * - onClose() : close handler
 */
export default function MonacoModalEditor({
  open,
  initialValue = "",
  language = "javascript",
  onSave = (code) => {},
  onClose = () => {},
  title = "Script Editor",
}) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const [value, setValue] = useState(initialValue);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    setValue(initialValue ?? "");
  }, [initialValue, open]);

  // register theme and completion provider once monaco is available
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // 1) Define a custom dark theme based on your colors
    monaco.editor.defineTheme("embedos-dark", {
      base: "vs-dark",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#0f172a",
        "editor.foreground": "#e2e8f0",
        "editorLineNumber.foreground": "#94a3b8",
        "editorLineNumber.activeForeground": "#e2e8f0",
        "editorLineNumber.background": "#0f172a",
        "editorCursor.foreground": "#ffffff",
        "editor.selectionBackground": "#1e293b",
      },
    });

    // 2) Basic custom completion/snippet provider
    monaco.languages.registerCompletionItemProvider(language, {
      provideCompletionItems: () => {
        const suggestions = [
          {
            label: "mqttConnect",
            kind: monaco.languages.CompletionItemKind.Snippet,
            documentation: "MQTT connect template",
            insertText:
              "const client = require('mqtt').connect('mqtt://HOST');\nclient.on('connect', () => {\n\tconsole.log('connected');\n});",
            insertTextRules:
              monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          },
          {
            label: "console.log",
            kind: monaco.languages.CompletionItemKind.Function,
            insertText: "console.log(${1:msg});",
            insertTextRules:
              monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "console.log snippet",
          },
        ];
        return { suggestions };
      },
    });

    // 3) Ctrl/Cmd + S binding (save)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      handleSave();
    });

    // 4) Format on paste/type options (these options are respected by Monaco)
    editor.updateOptions({
      formatOnPaste: true,
      formatOnType: true,
    });

    // 5) Resize built-in
    editor.layout();

    // 6) Lightweight syntax check on content change (see below)
    // We'll call `validateAndSetMarkers` from onChange (debounced)
  };

  // Basic syntax-check & set markers using JS Function() parse (fallback)
  // This is not a full linter but catches syntax errors and sets marker positions if possible.
  const validateAndSetMarkers = (code) => {
    const monaco = monacoRef.current;
    const editor = editorRef.current;
    if (!monaco || !editor) return;

    const model = editor.getModel();
    // Clear previous markers
    monaco.editor.setModelMarkers(model, "owner", []);

    try {
      // Attempt to parse quickly by using Function constructor
      // It will throw a SyntaxError with message including line:col in many environments,
      // but not all engines show line/col. We still catch error and try to parse message.
      /* eslint-disable no-new-func */
      new Function(code);
      /* eslint-enable no-new-func */
      // no syntax error — markers cleared
    } catch (err) {
      // Simple extraction of line/column if present in message (best-effort)
      // Example message: "Unexpected token ) in <anonymous>:3:10"
      const msg = (err && err.message) || String(err);
      let lineNumber = 1;
      let column = 1;

      const match = msg.match(/:(\d+):(\d+)/); // :line:col
      if (match) {
        lineNumber = parseInt(match[1], 10);
        column = parseInt(match[2], 10);
      } else if (err.lineNumber) {
        lineNumber = err.lineNumber;
        column = err.columnNumber || 1;
      }

      const marker = [
        {
          startLineNumber: Math.max(lineNumber, 1),
          startColumn: Math.max(column, 1),
          endLineNumber: Math.max(lineNumber, 1),
          endColumn: Math.max(column + 1, 2),
          message: msg,
          severity: monaco.MarkerSeverity.Error,
        },
      ];

      monaco.editor.setModelMarkers(model, "owner", marker);
    }
  };

  // Debounced validation while typing
  useEffect(() => {
    if (!editorRef.current) return;
    const timer = setTimeout(() => {
      validateAndSetMarkers(value);
      setIsTyping(false);
    }, 400); // validate 400ms after typing stops

    return () => clearTimeout(timer);
  }, [value]);

  const handleSave = () => {
    const code = editorRef.current ? editorRef.current.getValue() : value;
    // Try formatting before save (best-effort)
    try {
      // run format action (monaco has builtin formatter for some languages)
      const action = editorRef.current.getAction(
        "editor.action.formatDocument"
      );
      if (action) action.run();
    } catch (e) {
      // ignore format errors
      // console.warn("format failed:", e);
    }

    // small delay to allow format action to complete (if any)
    setTimeout(() => {
      const final = editorRef.current ? editorRef.current.getValue() : value;
      onSave(final);
    }, 150);
  };

  // expose Save on external Save button too
  const onSaveClick = () => {
    handleSave();
  };

  // close handler
  const onCloseClick = () => {
    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1400,
      }}
      onClick={onCloseClick}
    >
      <div
        style={{
          width: "90vw",
          height: "90vh",
          background: "#0f172a",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#e2e8f0",
            marginBottom: 10,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 1,
              mb: 1,
            }}
          >
            {/* FORMAT BUTTON */}
            <Button
              variant="outlined"
              onClick={() => {
                try {
                  const action = editorRef.current.getAction(
                    "editor.action.formatDocument"
                  );
                  if (action) action.run();
                } catch (e) {}
              }}
              sx={{
                borderColor: "#475569",
                color: "#e2e8f0",
                textTransform: "none",
                px: 2,
                py: 1,
                borderRadius: "8px",
                "&:hover": {
                  borderColor: "#64748b",
                  background: "rgba(255,255,255,0.06)",
                },
              }}
            >
              Format
            </Button>

            {/* SAVE BUTTON */}
            <Button
              variant="contained"
              onClick={onSaveClick}
              sx={{
                background: "#3b82f6",
                color: "white",
                textTransform: "none",
                px: 2,
                py: 1,
                borderRadius: "8px",
                fontWeight: 600,
                "&:hover": {
                  background: "#2563eb",
                },
              }}
            >
              Save (Ctrl+S)
            </Button>

            {/* CLOSE BUTTON */}
            <Button
              variant="contained"
              onClick={onCloseClick}
              sx={{
                background: "#ef4444",
                color: "white",
                textTransform: "none",
                px: 2,
                py: 1,
                borderRadius: "8px",
                fontWeight: 600,
                "&:hover": {
                  background: "#dc2626",
                },
              }}
            >
              Close
            </Button>
          </Stack>
        </div>

        <div style={{ flex: 1, overflow: "hidden", borderRadius: 8 }}>
          <Editor
            height="100%"
            defaultLanguage={language}
            language={language}
            value={value}
            theme="embedos-dark"
            onMount={handleEditorDidMount}
            onChange={(v) => {
              setIsTyping(true);
              setValue(v ?? "");
            }}
            options={{
              minimap: { enabled: true },
              wordWrap: "on",
              automaticLayout: true,
              fontSize: 13,
              scrollBeyondLastLine: false,
              renderLineHighlight: "line",
              formatOnPaste: true,
              formatOnType: true,
              // other options you may like:
              // glyphMargin: true,
              // folding: true,
            }}
          />
        </div>
      </div>
    </div>
  );
}
